import { useState, useEffect, useRef } from "react";
import { PublicLayout } from "@/components/layout/PublicLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { api, formatApiErrorDetail } from "@/lib/api";
import { PROGRAMS, REDEMPTION_METHODS, fmtUSD } from "@/data/points";
import { GUIDES } from "@/data/guides";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  Calculator, TrendingUp, Sparkles, CheckCircle2, ShieldCheck, ArrowRight,
  Plane, CreditCard, Gift, Banknote, Trophy, Star, BookOpen,
} from "lucide-react";

/* Count-up animated number */
function useCountUp(target, duration = 550) {
  const [val, setVal] = useState(target);
  const prev = useRef(target);
  useEffect(() => {
    const from = prev.current;
    const to = target;
    const start = performance.now();
    let raf;
    const tick = (now) => {
      const t = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - t, 3);
      setVal(from + (to - from) * eased);
      if (t < 1) raf = requestAnimationFrame(tick);
      else prev.current = to;
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, duration]);
  return val;
}

const methodIcons = {
  transfer: Plane,
  travel: CreditCard,
  cash: Banknote,
  giftcards: Gift,
};

const empty = { first_name: "", last_name: "", email: "", phone: "", points_balance: "" };

const CONSENT_INTRO =
  "By checking this box and submitting this form, I agree to receive marketing emails and SMS/text messages from Peak Points about credit-card and travel rewards tips, points valuations, and related offers, at the email address and phone number provided, including messages sent using automated technology. Consent is not a condition of any purchase. Message and data rates may apply. Reply STOP to opt out of SMS.";

export default function Home() {
  // Calculator state
  const [points, setPoints] = useState(50000);
  const [programId, setProgramId] = useState("chase");
  const program = PROGRAMS.find((p) => p.id === programId) || PROGRAMS[0];
  const low = (Number(points) || 0) * program.lowCpp / 100;
  const high = (Number(points) || 0) * program.highCpp / 100;
  const animLow = useCountUp(low);
  const animHigh = useCountUp(high);

  // Subscribe form
  const [form, setForm] = useState(empty);
  const [subProgram, setSubProgram] = useState("chase");
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!consent) {
      toast.error("Please agree to the consent notice to subscribe.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/registrations", {
        ...form,
        rewards_program: PROGRAMS.find((p) => p.id === subProgram)?.name || subProgram,
        tcpa_consent: consent,
        source_url: window.location.href,
      });
      setDone(true);
      toast.success("You're subscribed to Peak Points!");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Subscription failed.");
    } finally {
      setSubmitting(false);
    }
  };

  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <PublicLayout>
      {/* ---------------- Hero + Calculator ---------------- */}
      <section className="relative overflow-hidden border-b border-border bg-background">
        <div className="pointer-events-none absolute -right-24 -top-24 h-[420px] w-[420px] rounded-full bg-primary/10 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-32 left-1/3 h-[360px] w-[360px] rounded-full bg-accent/10 blur-3xl" />
        <div className="relative mx-auto grid max-w-7xl items-center gap-12 px-6 py-16 md:py-24 lg:grid-cols-2 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
          >
            <span className="inline-flex items-center gap-2 rounded-full border border-secondary/40 bg-secondary/20 px-3 py-1 font-heading text-xs font-bold uppercase tracking-widest text-yellow-700">
              <Sparkles className="h-3.5 w-3.5" /> Points & miles, decoded
            </span>
            <h1 className="mt-5 font-heading text-4xl font-black leading-[1.05] tracking-tighter text-slate-900 sm:text-5xl lg:text-6xl">
              Know what your points are{" "}
              <span className="text-primary">actually worth.</span>
            </h1>
            <p className="mt-5 max-w-lg font-body text-base leading-relaxed text-slate-600 md:text-lg">
              Make your points go further. Free calculators and simple guides to
              help you squeeze the most value out of credit-card and travel rewards.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button
                data-testid="hero-calculator-cta"
                onClick={() => scrollTo("calculator")}
                className="rounded-full bg-primary px-7 py-6 font-heading text-base font-bold text-primary-foreground shadow-[0_8px_24px_rgba(37,99,235,0.28)] transition-transform hover:-translate-y-0.5 hover:bg-blue-700"
              >
                Try the calculator <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
              <Button
                data-testid="hero-subscribe-cta"
                variant="outline"
                onClick={() => scrollTo("subscribe")}
                className="rounded-full border-slate-200 px-7 py-6 font-heading text-base font-bold text-slate-700 hover:border-primary hover:text-primary"
              >
                Get free tips
              </Button>
            </div>
            <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2 font-body text-sm text-slate-500">
              <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-accent" /> 14 programs</span>
              <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-accent" /> No signup to calculate</span>
              <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-accent" /> Unbiased estimates</span>
            </div>
          </motion.div>

          {/* Calculator widget */}
          <motion.div
            id="calculator"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45, delay: 0.1 }}
            className="rounded-3xl border border-border bg-card p-6 shadow-[0_24px_60px_-20px_rgba(15,23,42,0.25)] md:p-8"
            data-testid="calculator-widget"
          >
            <div className="flex items-center gap-2">
              <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary/10 text-primary">
                <Calculator className="h-5 w-5" />
              </span>
              <h2 className="font-heading text-lg font-bold text-slate-900">Points Value Calculator</h2>
            </div>

            <div className="mt-6 space-y-5">
              <div>
                <Label htmlFor="points" className="mb-2 block font-heading text-xs font-bold uppercase tracking-[0.15em] text-slate-500">
                  How many points do you have?
                </Label>
                <Input
                  id="points"
                  data-testid="calculator-points-input"
                  type="number"
                  min={0}
                  value={points}
                  onChange={(e) => setPoints(e.target.value)}
                  placeholder="50,000"
                  className="h-14 rounded-xl border-slate-200 text-lg font-semibold focus-visible:ring-primary"
                />
              </div>
              <div>
                <Label className="mb-2 block font-heading text-xs font-bold uppercase tracking-[0.15em] text-slate-500">
                  Rewards program
                </Label>
                <Select value={programId} onValueChange={setProgramId}>
                  <SelectTrigger data-testid="calculator-program-select" className="h-14 rounded-xl border-slate-200 text-base font-semibold">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="max-h-72">
                    {PROGRAMS.map((p) => (
                      <SelectItem key={p.id} value={p.id} data-testid={`program-option-${p.id}`}>
                        {p.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Result */}
            <div className="mt-6 rounded-2xl bg-accent/10 p-5" data-testid="calculator-result">
              <p className="font-heading text-xs font-bold uppercase tracking-[0.15em] text-emerald-700">
                Estimated value
              </p>
              <p className="mt-1 font-heading text-4xl font-black tracking-tight text-emerald-700 sm:text-5xl" data-testid="calculator-result-range">
                {fmtUSD(animLow)}<span className="text-slate-400"> – </span>{fmtUSD(animHigh)}
              </p>
              <p className="mt-2 flex items-start gap-1.5 font-body text-sm text-slate-600">
                <TrendingUp className="mt-0.5 h-4 w-4 shrink-0 text-accent" />
                {program.tip}
              </p>
            </div>

            <p className="mt-4 font-body text-xs leading-relaxed text-slate-400">
              Based on {program.lowCpp}¢–{program.highCpp}¢ per point. Cashing out isn't always the best deal —{" "}
              <button onClick={() => scrollTo("compare")} className="font-semibold text-primary underline underline-offset-2" data-testid="see-options-link">
                see your options
              </button>.
            </p>
          </motion.div>
        </div>
      </section>

      {/* ---------------- Comparison ---------------- */}
      <section id="compare" className="mx-auto max-w-7xl px-6 py-16 md:py-24 lg:px-8">
        <div className="max-w-2xl">
          <p className="font-heading text-xs font-bold uppercase tracking-[0.2em] text-primary">Best ways to use points</p>
          <h2 className="mt-3 font-heading text-3xl font-black tracking-tighter text-slate-900 sm:text-4xl">
            Cash back vs travel vs gift cards
          </h2>
          <p className="mt-3 font-body text-slate-600">
            The same points can be worth wildly different amounts depending on how
            you redeem. Here's the typical value per point for each option.
          </p>
        </div>

        <div className="mt-10 grid gap-5 md:grid-cols-2 lg:grid-cols-4">
          {REDEMPTION_METHODS.map((m, i) => {
            const Icon = methodIcons[m.id];
            return (
              <motion.div
                key={m.id}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.3, delay: i * 0.06 }}
                data-testid={`comparison-card-${m.id}`}
                className={`relative flex flex-col rounded-3xl border p-6 ${
                  m.best
                    ? "border-accent/40 bg-accent/10 shadow-[0_16px_40px_-20px_rgba(16,185,129,0.5)]"
                    : "border-border bg-card"
                }`}
              >
                {m.best && (
                  <span className="absolute right-4 top-4 inline-flex items-center gap-1 rounded-full bg-accent px-2.5 py-1 font-heading text-[10px] font-bold uppercase tracking-wide text-accent-foreground">
                    <Trophy className="h-3 w-3" /> Best
                  </span>
                )}
                <span className={`flex h-11 w-11 items-center justify-center rounded-xl ${m.best ? "bg-accent/20 text-emerald-700" : "bg-primary/10 text-primary"}`}>
                  <Icon className="h-5 w-5" />
                </span>
                <h3 className="mt-4 font-heading text-lg font-bold text-slate-900">{m.label}</h3>
                <p className="mt-1 font-heading text-3xl font-black tracking-tight text-slate-900">
                  {m.cpp.toFixed(2)}<span className="text-lg text-slate-400">¢/pt</span>
                </p>
                <span className={`mt-3 inline-flex w-fit items-center gap-1 rounded-full px-2.5 py-1 font-heading text-xs font-bold ${
                  m.best ? "bg-accent/20 text-emerald-700" : "bg-muted text-slate-600"
                }`}>
                  <Star className="h-3 w-3" /> {m.verdict}
                </span>
                <p className="mt-3 font-body text-sm leading-relaxed text-slate-600">{m.note}</p>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* ---------------- Guides teaser ---------------- */}
      <section className="border-t border-border bg-white">
        <div className="mx-auto max-w-7xl px-6 py-16 md:py-20 lg:px-8">
          <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-end">
            <div className="max-w-xl">
              <p className="font-heading text-xs font-bold uppercase tracking-[0.2em] text-primary">Points guides</p>
              <h2 className="mt-3 font-heading text-3xl font-black tracking-tighter text-slate-900 sm:text-4xl">
                Learn the basics in a few minutes
              </h2>
              <p className="mt-3 font-body text-slate-600">
                Short, plain-English reads that help you get more from every point.
              </p>
            </div>
            <Link to="/guides" data-testid="view-all-guides" className="inline-flex items-center gap-1 font-heading text-sm font-bold text-primary hover:underline">
              View all guides <ArrowRight className="h-4 w-4" />
            </Link>
          </div>

          <div className="mt-8 grid gap-5 md:grid-cols-3">
            {GUIDES.slice(0, 3).map((g) => (
              <Link
                key={g.slug}
                to={`/guides/${g.slug}`}
                data-testid={`home-guide-${g.slug}`}
                className="group flex flex-col rounded-3xl border border-border bg-card p-6 transition-transform duration-200 hover:-translate-y-1 hover:border-primary/40"
              >
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10 text-primary">
                  <BookOpen className="h-5 w-5" />
                </span>
                <h3 className="mt-4 font-heading text-lg font-bold tracking-tight text-slate-900 group-hover:text-primary">{g.title}</h3>
                <p className="mt-2 font-body text-sm leading-relaxed text-slate-600">{g.dek}</p>
                <span className="mt-4 inline-flex items-center gap-1 font-heading text-sm font-bold text-primary">
                  Read <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ---------------- Subscribe / Lead capture ---------------- */}
      <section id="subscribe" className="border-t border-border bg-white">
        <div className="mx-auto grid max-w-7xl gap-12 px-6 py-16 md:py-24 lg:grid-cols-2 lg:px-8">
          <div>
            <p className="font-heading text-xs font-bold uppercase tracking-[0.2em] text-primary">Free newsletter</p>
            <h2 className="mt-3 font-heading text-3xl font-black tracking-tighter text-slate-900 sm:text-4xl">
              Get the best points moves in your inbox
            </h2>
            <p className="mt-4 font-body leading-relaxed text-slate-600">
              Join free and we'll send you simple, no-fluff tips on getting more
              from your credit-card and travel rewards — plus occasional SMS alerts
              on the best transfer bonuses and promotions.
            </p>
            <ul className="mt-6 space-y-3 font-body text-sm text-slate-700">
              <li className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-accent" /> Points valuations, updated regularly</li>
              <li className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-accent" /> Transfer-bonus and promo alerts</li>
              <li className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-accent" /> Unsubscribe anytime, no spam</li>
            </ul>
            <p className="mt-6 flex items-center gap-2 font-body text-sm text-slate-500">
              <ShieldCheck className="h-4 w-4 text-primary" /> Your details are stored securely and never sold.
            </p>
          </div>

          <div>
            {done ? (
              <div className="rounded-3xl border border-border bg-card p-10 text-center shadow-sm" data-testid="subscribe-success">
                <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-accent/15 text-accent">
                  <CheckCircle2 className="h-8 w-8" />
                </span>
                <h3 className="mt-6 font-heading text-2xl font-bold text-slate-900">You're in! 🎉</h3>
                <p className="mt-3 font-body text-slate-600">
                  Thanks for subscribing. Keep an eye on your inbox — the best
                  points tips are on their way.
                </p>
                <Button
                  variant="outline"
                  className="mt-6 rounded-full border-slate-200 font-heading font-semibold text-slate-700 hover:border-primary hover:text-primary"
                  data-testid="subscribe-reset-button"
                  onClick={() => { setForm(empty); setConsent(false); setDone(false); }}
                >
                  Add another
                </Button>
              </div>
            ) : (
              <form onSubmit={submit} className="rounded-3xl border border-border bg-card p-6 shadow-[0_24px_60px_-20px_rgba(15,23,42,0.2)] md:p-8" data-testid="subscribe-form">
                <div className="grid gap-5 sm:grid-cols-2">
                  <Field label="First name" id="first_name">
                    <Input data-testid="input-first-name" required value={form.first_name} onChange={set("first_name")} placeholder="Jane" className="h-12 rounded-xl border-slate-200" />
                  </Field>
                  <Field label="Last name" id="last_name">
                    <Input data-testid="input-last-name" required value={form.last_name} onChange={set("last_name")} placeholder="Doe" className="h-12 rounded-xl border-slate-200" />
                  </Field>
                  <div className="sm:col-span-2">
                    <Field label="Email" id="email">
                      <Input data-testid="input-email" required type="email" value={form.email} onChange={set("email")} placeholder="jane@example.com" className="h-12 rounded-xl border-slate-200" />
                    </Field>
                  </div>
                  <div className="sm:col-span-2">
                    <Field label="Mobile phone (for SMS tips)" id="phone">
                      <Input data-testid="input-phone" required type="tel" value={form.phone} onChange={set("phone")} placeholder="(555) 123-4567" className="h-12 rounded-xl border-slate-200" />
                    </Field>
                  </div>
                  <Field label="Main rewards program" id="rewards_program">
                    <Select value={subProgram} onValueChange={setSubProgram}>
                      <SelectTrigger data-testid="input-rewards-program" className="h-12 rounded-xl border-slate-200">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="max-h-72">
                        {PROGRAMS.map((p) => (
                          <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field label="Points balance (optional)" id="points_balance">
                    <Input data-testid="input-points-balance" value={form.points_balance} onChange={set("points_balance")} placeholder="~50,000" className="h-12 rounded-xl border-slate-200" />
                  </Field>
                </div>

                {/* Consent */}
                <div className="mt-6 rounded-2xl bg-muted p-5">
                  <div className="flex items-start gap-3">
                    <Checkbox
                      id="tcpa"
                      data-testid="tcpa-consent-checkbox"
                      checked={consent}
                      onCheckedChange={(v) => setConsent(!!v)}
                      className="mt-0.5 h-5 w-5 border-slate-300 data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground"
                    />
                    <Label htmlFor="tcpa" className="cursor-pointer font-body text-xs leading-relaxed text-slate-500">
                      {CONSENT_INTRO}{" "}
                      I have read and agree to the{" "}
                      <Link to="/terms" className="font-semibold text-primary underline">Terms</Link>,{" "}
                      <Link to="/privacy" className="font-semibold text-primary underline">Privacy Policy</Link>, and{" "}
                      <Link to="/sms-terms" className="font-semibold text-primary underline">SMS Terms</Link>.
                    </Label>
                  </div>
                </div>

                <Button
                  type="submit"
                  data-testid="subscribe-submit-button"
                  disabled={submitting}
                  className="mt-6 w-full rounded-full bg-primary py-6 font-heading text-base font-bold text-primary-foreground shadow-[0_8px_24px_rgba(37,99,235,0.28)] transition-transform hover:-translate-y-0.5 hover:bg-blue-700"
                >
                  {submitting ? "Subscribing…" : "Get free points tips"}
                </Button>
              </form>
            )}
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}

const Field = ({ label, id, children }) => (
  <div>
    <Label htmlFor={id} className="mb-2 block font-heading text-xs font-bold uppercase tracking-[0.15em] text-slate-500">
      {label}
    </Label>
    {children}
  </div>
);
